import React, { useEffect, useMemo, useState } from "react";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import MaInput from "../../../../Components/UI/ma-input";
import {
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { LuSearch } from "react-icons/lu";
import { PiListDashes } from "react-icons/pi";
import { BsUiRadiosGrid } from "react-icons/bs";
import { ProductThumb } from "../dashboard/recent-order";
import StatusChip from "./status-chip";
import folder from "../../../../assets/Icons/dashboard/folder.png";
import MaButton from "../../../../Components/UI/ma-button";
import { SimplePagination } from "../../../../Components/UI/simple-pagination";
import { ProductActionsMenu } from "./product-actions-menu";
import { PRODUCT_FILTER_MENU_PROPS } from "./product-filter-menu-styles";
import ProductViewDrawer from "./product-view-drawer";
import StockUpModal from "./stock-up-modal";
import {
  GET_SELLER_PRODUCT_STATS,
  GET_SELLER_PRODUCTS,
} from "../../../../Utils/Queries";
import { STOCK_UP_PRODUCT } from "../../../../Utils/Mutations";
import {
  mapSellerProductToDrawerDetail,
  mapSellerProductToListRow,
} from "./seller-product-mapper";

function ProductGridCard({ product, onViewProduct }) {
  const hasImage = Boolean(product.image);
  return (
    <article className="flex flex-col" onClick={() => onViewProduct(product)}>
      <div
        className={`relative aspect-square rounded-xl overflow-hidden h-[200px] p-3 box-border bg-[#F8F8F8] ${
          hasImage ? "bg-cover bg-center" : ""
        }`}
        style={
          hasImage ? { backgroundImage: `url(${product.image})` } : undefined
        }
      >
        {hasImage ? (
          <div
            className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/45 via-black/10 to-transparent"
            aria-hidden
          />
        ) : null}
        <div className="relative z-[1]">
          <StatusChip status={product.status} />
        </div>
      </div>
      <div className="mt-3 flex flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="my-0 text-[14px] font-bold text-[#3D3D3D] leading-snug">
            {product.name}
          </h3>
          <span className="text-[14px] font-bold text-gray-900 shrink-0">
            {product.price}
          </span>
        </div>
        <span className="w-fit rounded-[9px] h-[22px] flex items-center justify-center bg-[#F8F8F8] px-2 text-[12px] font-medium text-[#0088FF]">
          {product.category}
        </span>
      </div>
    </article>
  );
}

const FILTER_SELECT_PROPS = {
  MenuProps: PRODUCT_FILTER_MENU_PROPS,
};

const STATUS_SELECT_PROPS = {
  ...FILTER_SELECT_PROPS,
  renderValue: (selected) => {
    if (selected === "") {
      return <span className="text-[#717171]">Status</span>;
    }
    const labels = {
      "sold-out": "Sold Out",
      "in-stock": "In-stock",
      "low-stock": "Low stock",
    };
    return labels[selected] ?? selected;
  },
};

const TABLE_COL_SPAN = 6;
const PAGE_SIZE = 10;

const getInitialView = () => {
  if (typeof window === "undefined") return "list";
  return window.matchMedia("(max-width: 899px)").matches ? "grid" : "list";
};

const ProductTable = ({ onOpenAddProduct, addProductDisabled = false }) => {
  const navigate = useNavigate();
  const client = useApolloClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [view, setView] = useState(getInitialView);
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [drawerProduct, setDrawerProduct] = useState(null);
  const [stockUpTarget, setStockUpTarget] = useState(null);
  const [listPage, setListPage] = useState(1);

  const { data, loading, error } = useQuery(GET_SELLER_PRODUCTS);

  const [runStockUpProduct] = useMutation(STOCK_UP_PRODUCT, {
    refetchQueries: [{ query: GET_SELLER_PRODUCTS }],
    awaitRefetchQueries: true,
  });

  const rows = useMemo(() => {
    const raw = data?.getSellerProducts?.items;
    const items = Array.isArray(raw) ? raw : [];
    return items.map(mapSellerProductToListRow);
  }, [data]);

  const filteredProducts = useMemo(() => {
    let list = rows;
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          String(p.sku).toLowerCase().includes(q)
      );
    }
    if (statusFilter) {
      list = list.filter((p) => p.status === statusFilter);
    }
    if (categoryFilter !== "all") {
      list = list.filter((p) =>
        p.category.toLowerCase().includes(categoryFilter)
      );
    }
    return list;
  }, [rows, search, statusFilter, categoryFilter]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredProducts.length / PAGE_SIZE)),
    [filteredProducts.length]
  );

  useEffect(() => {
    if (isMobile) setView("grid");
  }, [isMobile]);

  useEffect(() => {
    setListPage(1);
  }, [search, statusFilter, categoryFilter]);

  useEffect(() => {
    setListPage((p) => Math.min(p, totalPages));
  }, [totalPages]);

  const safeListPage = Math.min(listPage, totalPages);

  const pagedProducts = useMemo(() => {
    const start = (safeListPage - 1) * PAGE_SIZE;
    return filteredProducts.slice(start, start + PAGE_SIZE);
  }, [filteredProducts, safeListPage]);

  const showCatalogEmpty = !loading && !error && rows.length === 0;
  const showFilteredEmpty =
    !loading && !error && rows.length > 0 && filteredProducts.length === 0;

  const showPaginationBar =
    !loading &&
    !error &&
    !showCatalogEmpty &&
    !showFilteredEmpty &&
    filteredProducts.length > 0;

  const isGridView = isMobile || view === "grid";

  return (
    <div className="bg-white rounded-[14px] border border-[#EFEFEF] p-4 sm:p-5 min-w-0">
      {/* Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="w-full md:w-[35%] md:min-w-[200px]">
          <MaInput
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <LuSearch className="text-[20px]" />
                  </InputAdornment>
                ),
              },
            }}
            placeholder="Search products..."
          />
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end md:gap-x-3 w-full md:w-auto">
          <div className="grid grid-cols-2 gap-3 w-full sm:w-auto sm:flex sm:items-center sm:gap-x-3">
            <div className="min-w-0">
              <MaInput
                fullWidth
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                select
                SelectProps={STATUS_SELECT_PROPS}
              >
                <MenuItem value="">Status</MenuItem>
                <MenuItem value="sold-out">Sold Out</MenuItem>
                <MenuItem value="in-stock">In-stock</MenuItem>
                <MenuItem value="low-stock">Low stock</MenuItem>
              </MaInput>
            </div>
            <div className="min-w-0">
              <MaInput
                fullWidth
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                select
                SelectProps={FILTER_SELECT_PROPS}
              >
                <MenuItem value="all">All Categories</MenuItem>
                <MenuItem value="accessories">Accessories</MenuItem>
                <MenuItem value="fashion">Fashion</MenuItem>
                <MenuItem value="gadget">Gadget</MenuItem>
              </MaInput>
            </div>
          </div>

          <div
            className="hidden md:flex h-[48px] shrink-0 rounded-full bg-[#F8F8F8] p-2 box-border gap-x-3 items-center"
            role="group"
            aria-label="Product layout"
          >
            <div
              onClick={() => setView("list")}
              aria-pressed={view === "list"}
              className={`flex h-full items-center gap-x-2 px-3 rounded-full text-[13px] transition-colors cursor-pointer border-0 font-inherit ${
                view === "list"
                  ? "bg-[#2E1834] text-white"
                  : "bg-transparent text-[#626262] hover:text-[#3D3D3D]"
              }`}
            >
              <PiListDashes className="text-[18px] shrink-0" aria-hidden />
              <span>List</span>
            </div>
            <div
              onClick={() => setView("grid")}
              aria-pressed={view === "grid"}
              className={`flex h-full items-center gap-x-2 px-3 rounded-full text-[13px] transition-colors cursor-pointer border-0 font-inherit ${
                view === "grid"
                  ? "bg-[#2E1834] text-white"
                  : "bg-transparent text-[#626262] hover:text-[#3D3D3D]"
              }`}
            >
              <BsUiRadiosGrid className="text-[14px] shrink-0" aria-hidden />
              <span>Grid</span>
            </div>
          </div>
        </div>
      </div>

      {error && !loading ? (
        <div className="mt-8 flex flex-col items-center justify-center px-4 py-16 text-center">
          <p className="text-[14px] font-semibold text-[#3D3D3D] my-0">
            Couldn&apos;t load products
          </p>
          <p className="text-[12.5px] text-[#6B7280] mt-2 my-0 max-w-[320px] leading-snug">
            Something went wrong while fetching your catalog. Try refreshing
            the page.
          </p>
        </div>
      ) : !isGridView ? (
        <Table sx={{ mt: 3 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: "#F8F8F8", borderRadius: "12px" }}>
              <TableCell
                sx={{
                  fontSize: "12px",
                  color: "#999999",
                  fontWeight: 600,
                  border: "none",
                }}
              >
                PRODUCT
              </TableCell>
              <TableCell
                sx={{
                  fontSize: "12px",
                  color: "#999999",
                  fontWeight: 600,
                  border: "none",
                  textTransform: "uppercase",
                }}
              >
                Category
              </TableCell>
              <TableCell
                sx={{
                  fontSize: "12px",
                  color: "#999999",
                  fontWeight: 600,
                  border: "none",
                  textTransform: "uppercase",
                }}
              >
                Price
              </TableCell>
              <TableCell
                sx={{
                  fontSize: "12px",
                  color: "#999999",
                  fontWeight: 600,
                  border: "none",
                  textTransform: "uppercase",
                }}
              >
                Stock
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontSize: "12px",
                  color: "#999999",
                  fontWeight: 600,
                  border: "none",
                  textTransform: "uppercase",
                }}
              >
                Status
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontSize: "12px",
                  color: "#999999",
                  fontWeight: 600,
                  border: "none",
                  textTransform: "uppercase",
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={TABLE_COL_SPAN}>
                  <div className="h-[240px] flex items-center justify-center">
                    <span className="text-[13px] text-[#9CA3AF]">
                      Loading products…
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            ) : showCatalogEmpty ? (
              <TableRow>
                <TableCell colSpan={TABLE_COL_SPAN}>
                  <div className="h-[300px] flex items-center justify-center flex-col">
                    <img src={folder} width={100} alt="" />
                    <span>No Product Yet</span>

                    <MaButton
                      variant={"primary"}
                      sx={{ mt: 3 }}
                      disabled={addProductDisabled}
                      onClick={() => onOpenAddProduct?.()}
                    >
                      Add Product
                    </MaButton>
                  </div>
                </TableCell>
              </TableRow>
            ) : showFilteredEmpty ? (
              <TableRow>
                <TableCell colSpan={TABLE_COL_SPAN}>
                  <div className="h-[200px] flex items-center justify-center flex-col px-4 text-center">
                    <span className="text-[13px] text-[#6B7280]">
                      No products match your search or filters.
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {pagedProducts.map((product) => (
                <TableRow key={product.id}>
                  <TableCell>
                    <div className="flex gap-x-3">
                      <ProductThumb src={product.image} alt="" />
                      <div>
                        <h3 className="my-0">{product.name}</h3>
                        <p className="my-0 text-[#626262] text-[10px]">
                          {product.sku}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{product.category}</TableCell>
                  <TableCell>{product.price}</TableCell>
                  <TableCell>{product.stock}</TableCell>
                  <TableCell align="center">
                    <div className="flex items-center justify-center">
                      <StatusChip status={product.status} />
                    </div>
                  </TableCell>
                  <TableCell align="center">
                    <div className="flex justify-center">
                      <ProductActionsMenu
                        product={product}
                        onViewProduct={(p) => {
                          if (p.raw) {
                            setDrawerProduct(
                              mapSellerProductToDrawerDetail(p.raw)
                            );
                          }
                        }}
                        onEditProduct={(p) =>
                          navigate(`/seller/product/edit/${p.id}`)
                        }
                        onStockUp={(p) => setStockUpTarget(p)}
                      />
                    </div>
                  </TableCell>
                </TableRow>
                ))}
              </>
            )}
          </TableBody>
        </Table>
      ) : (
        <div className="mt-6">
          {loading ? (
            <div className="h-[240px] flex items-center justify-center">
              <span className="text-[13px] text-[#9CA3AF]">
                Loading products…
              </span>
            </div>
          ) : error ? null : showCatalogEmpty ? (
            <div className="h-[300px] flex items-center justify-center flex-col">
              <img src={folder} width={100} alt="" />
              <span>No Product Yet</span>
              <MaButton
                variant={"primary"}
                sx={{ mt: 3 }}
                disabled={addProductDisabled}
                onClick={() => onOpenAddProduct?.()}
              >
                Add Product
              </MaButton>
            </div>
          ) : showFilteredEmpty ? (
            <div className="h-[200px] flex items-center justify-center px-4 text-center">
              <span className="text-[13px] text-[#6B7280]">
                No products match your search or filters.
              </span>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-5">
              {pagedProducts.map((product) => (
                <ProductGridCard key={product.id} product={product}   onViewProduct={(p) => {
                  if (p.raw) {
                    setDrawerProduct(
                      mapSellerProductToDrawerDetail(p.raw)
                    );
                  }
                }}/>
              ))}
            </div>
          )}
        </div>
      )}

      {showPaginationBar ? (
        <SimplePagination
          className="mt-8 border-t border-[#EFEFEF] pt-5"
          page={safeListPage}
          totalPages={totalPages}
          onPageChange={setListPage}
        />
      ) : null}

      <ProductViewDrawer
        open={drawerProduct != null}
        productId={null}
        detail={drawerProduct}
        onClose={() => setDrawerProduct(null)}
        onProductDeleted={() => {
          client.refetchQueries({
            include: [GET_SELLER_PRODUCTS, GET_SELLER_PRODUCT_STATS],
          });
        }}
        onStockUp={(summary) => {
          setStockUpTarget({
            id: summary.id,
            name: summary.name,
            stock: summary.stock,
          });
          setDrawerProduct(null);
        }}
      />

      <StockUpModal
        open={stockUpTarget != null}
        product={stockUpTarget}
        onClose={() => setStockUpTarget(null)}
        onConfirm={async ({ productId, quantity }) => {
          await runStockUpProduct({
            variables: { productId, quantity },
          });
        }}
      />
    </div>
  );
};

export default ProductTable;
